"use client";

import {
  twelveWeekIntro,
  twelveWeekProgramPhases,
  twelveWeekWorkouts,
} from "@12-week/lib/12-week";
import { twelveWeekExercises } from "@12-week/lib/exercises";
import {
  hypertrophyIntro,
  hypertrophyWorkouts,
} from "@12-week/lib/hypertrophy";
import { ProgramNavigationProps } from "@12-week/lib/types";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import LinkButton from "@packages/components/ui/LinkButton";

const programData = {
  "12-week": {
    entries: twelveWeekProgramPhases,
    intro: twelveWeekIntro,
    introHref: "/12-week",
    entryHref: (slug: string) => `/12-week/${slug}`,
    introLabel: "Back to Intro",
    overline: "X3 12-Week Program",
  },
  hypertrophy: {
    entries: hypertrophyWorkouts,
    intro: hypertrophyIntro,
    introHref: "/x3-hypertrophy",
    entryHref: (slug: string) => `/x3-hypertrophy/${slug}`,
    introLabel: "Back to Intro",
    overline: "X3 Hypertrophy Program",
  },
  nutrition: {
    entries: {},
    intro: {},
    introHref: "",
    entryHref: () => "",
    introLabel: "Back to Program Intro",
    overline: "Nutrition Program",
  },
  exercises: {
    entries: twelveWeekExercises,
    intro: {},
    introHref: "/x3-exercises",
    entryHref: (slug: string) => `/x3-exercises/${slug}`,
    introLabel: "Back to X3 Exercises Overview",
    overline: "X3 Exercises",
  },
};

export default function ProgramNavigation({
  currentSlug,
  program,
  nutritionEntries,

  nutritionIntroHref,
  nutritionEntryHref,
  nutritionIntroLabel,
  fromProgram,
  fromWeek,
}: ProgramNavigationProps) {
  // Handle custom nutrition navigation via props
  if (
    program === "nutrition" &&
    nutritionEntries &&
    nutritionEntryHref !== undefined
  ) {
    const entries = nutritionEntries;
    const introHref = nutritionIntroHref || "";

    // Normalize incoming base and build safe entry href to avoid double "/videos"
    const baseRaw = (nutritionEntryHref || "").replace(/\/+$/g, ""); // remove trailing slashes
    const base = baseRaw; // keep leading slash if present

    const entryHref = (slug: string) => {
      if (!base) return "";
      // If base already ends with "/videos" (or "/videos/..."), append just the slug
      if (/\/videos(\/|$)/.test(base)) {
        return `${base}/${slug}`.replace(/\/+/g, "/");
      }
      // Otherwise append the videos segment
      return `${base}/videos/${slug}`.replace(/\/+/g, "/");
    };

    const introLabel = nutritionIntroLabel || "Back to Nutrition Program Intro";
    const slugs = Object.keys(entries);
    const currentIdx = slugs.indexOf(currentSlug);

    const isIntro = currentSlug === "/" || currentSlug === "";

    // Previous logic
    let prevHref: string | null = null;
    let prevLabel: string | null = null;
    if (isIntro) {
      prevHref = null;
      prevLabel = null;
    } else if (currentIdx === 0) {
      prevHref = introHref;
      prevLabel = introLabel;
    } else if (currentIdx > 0) {
      const prevSlug = slugs[currentIdx - 1];
      prevHref = entryHref(prevSlug);
      prevLabel = `Back to ${(entries as Record<string, { title: string }>)[prevSlug].title}`;
    }

    // Next logic
    let nextHref: string | null = null;
    let nextLabel: string | null = null;
    if (isIntro && slugs.length > 0) {
      const firstSlug = slugs[0];
      nextHref = entryHref(firstSlug);
      nextLabel = `Go to ${(entries as Record<string, { title: string }>)[firstSlug].title}`;
    } else if (currentIdx < slugs.length - 1 && currentIdx !== -1) {
      const nextSlug = slugs[currentIdx + 1];
      nextHref = entryHref(nextSlug);
      nextLabel = `Go to ${(entries as Record<string, { title: string }>)[nextSlug].title}`;
    }

    return (
      <nav
        className="mt-12 flex flex-wrap items-center justify-between gap-4"
        aria-label="Program navigation"
      >
        {prevHref ? (
          <LinkButton href={prevHref} variant="outline" size="lg">
            <ChevronLeftIcon className="mr-2 inline-block h-5 w-5 align-middle" />
            {prevLabel}
          </LinkButton>
        ) : (
          <span />
        )}
        {nextHref ? (
          <LinkButton href={nextHref} variant="outline" size="lg">
            {nextLabel}
            <ChevronRightIcon className="ml-2 inline-block h-5 w-5 align-middle" />
          </LinkButton>
        ) : (
          <span />
        )}
      </nav>
    );
  }

  const rawEntries = programData[program].entries as Record<
    string,
    { title?: string }
  >;
  const entries = Object.fromEntries(
    Object.entries(rawEntries).map(([slug, entry]) => [
      slug,
      { title: entry.title ?? "" },
    ]),
  );
  const introHref = programData[program].introHref;
  const entryHref = programData[program].entryHref;
  const introLabel = programData[program].introLabel;
  const slugs = Object.keys(entries);
  const currentIdx = slugs.indexOf(currentSlug);

  const isIntro = currentSlug === "/" || currentSlug === "";

  // Previous logic
  let prevHref: string | null = null;
  let prevLabel: string | null = null;
  if (isIntro) {
    prevHref = null;
    prevLabel = null;
  } else if (currentIdx === 0) {
    prevHref = introHref;
    prevLabel = introLabel;
  } else if (currentIdx > 0) {
    const prevSlug = slugs[currentIdx - 1];
    prevHref = entryHref(prevSlug);
    prevLabel = `Back to ${(entries as Record<string, { title: string }>)[prevSlug].title}`;
  }

  // Next logic
  let nextHref: string | null = null;
  let nextLabel: string | null = null;
  if (isIntro && slugs.length > 0) {
    const firstSlug = slugs[0];
    nextHref = entryHref(firstSlug);
    nextLabel = `Go to ${(entries as Record<string, { title: string }>)[firstSlug].title}`;
  } else if (currentIdx < slugs.length - 1 && currentIdx !== -1) {
    const nextSlug = slugs[currentIdx + 1];
    nextHref = entryHref(nextSlug);
    nextLabel = `Go to ${(entries as Record<string, { title: string }>)[nextSlug].title}`;
  }

  // Contextual back button for exercises
  if (program === "exercises" && fromProgram === "12-week" && fromWeek) {
    // Use the actual week title from twelveWeekProgramPhases if available
    const weekTitle = twelveWeekProgramPhases[fromWeek]?.title || fromWeek;

    // Build workout-ordered exercise list (Push Day + Pull Day)
    const orderedExercises = [
      ...twelveWeekWorkouts["push-day"].exercises.map((ex) => ex.slug),
      ...twelveWeekWorkouts["pull-day"].exercises.map((ex) => ex.slug),
    ];

    // Find the next exercise in the workout-specific order
    const currentIdx = orderedExercises.indexOf(currentSlug);
    let nextHref: string | null = null;
    let nextLabel: string | null = null;
    if (currentIdx < orderedExercises.length - 1 && currentIdx !== -1) {
      const nextSlug = orderedExercises[currentIdx + 1];
      // Preserve query params for next link
      nextHref = `/x3-exercises/${nextSlug}?from=${encodeURIComponent(fromProgram)}&week=${encodeURIComponent(fromWeek)}`;
      nextLabel = `Go to ${twelveWeekExercises[nextSlug].title}`;
    }
    return (
      <nav
        className="mt-12 flex flex-wrap items-center justify-between gap-4"
        aria-label="Program navigation"
      >
        <div className="order-1 flex-1 sm:flex sm:justify-start">
          <LinkButton
            href={`/12-week/${fromWeek}`}
            variant="ghost"
            size="lg"
            className="truncate"
          >
            <ChevronLeftIcon className="mr-2 inline-block h-5 w-5 align-middle" />
            {`Back to ${weekTitle}`}
          </LinkButton>
        </div>
        <div className="order-3 flex w-full justify-center">
          <LinkButton
            href="/x3-exercises"
            variant="grayLink"
            size="lg"
            className="truncate"
          >
            See All X3 Exercises
          </LinkButton>
        </div>
        <div className="order-2 flex-1 sm:flex sm:justify-end">
          {nextHref ? (
            <LinkButton
              href={nextHref}
              variant="ghost"
              size="lg"
              className="truncate"
            >
              {nextLabel}
              <ChevronRightIcon className="ml-2 inline-block h-5 w-5 align-middle" />
            </LinkButton>
          ) : (
            <span />
          )}
        </div>
      </nav>
    );
  }

  if (program === "exercises") {
    return (
      <nav
        className="mt-12 flex flex-wrap items-center justify-between gap-4"
        aria-label="Program navigation"
      >
        <div className="order-1 flex-1 sm:flex sm:justify-start">
          {prevHref ? (
            <LinkButton
              href={prevHref}
              variant="ghost"
              size="lg"
              className="truncate"
            >
              <ChevronLeftIcon className="mr-2 inline-block h-5 w-5 align-middle" />
              {prevLabel}
            </LinkButton>
          ) : (
            <span />
          )}
        </div>
        <div className="order-3 flex w-full justify-center">
          <LinkButton
            href="/x3-exercises"
            variant="grayLink"
            size="lg"
            className="truncate"
          >
            See All X3 Exercises
          </LinkButton>
        </div>
        <div className="order-2 flex-1 sm:flex sm:justify-end">
          {nextHref ? (
            <LinkButton
              href={nextHref}
              variant="ghost"
              size="lg"
              className="truncate"
            >
              {nextLabel}
              <ChevronRightIcon className="ml-2 inline-block h-5 w-5 align-middle" />
            </LinkButton>
          ) : (
            <span />
          )}
        </div>
      </nav>
    );
  }

  return (
    <nav
      className="mt-12 flex flex-wrap items-center justify-between gap-4"
      aria-label="Program navigation"
    >
      {prevHref ? (
        <LinkButton
          href={prevHref}
          variant="ghost"
          size="lg"
          className="truncate"
        >
          <ChevronLeftIcon className="mr-2 inline-block h-5 w-5 align-middle" />
          {prevLabel}
        </LinkButton>
      ) : (
        <span />
      )}
      {nextHref ? (
        <LinkButton
          href={nextHref}
          variant="ghost"
          size="lg"
          className="truncate"
        >
          {nextLabel}
          <ChevronRightIcon className="ml-2 inline-block h-5 w-5 align-middle" />
        </LinkButton>
      ) : (
        <span />
      )}
    </nav>
  );
}
